// src/hooks/useCalendarItems.ts
// 統一的日曆項目 Hook，整合 TimeBox 和 Event
// 🔥 Local-first：先從 IndexedDB 載入，再背景同步

import { useState, useCallback, useEffect, useRef } from 'react';
import { ScheduleItem } from '../../../types';
import { TimeBox } from '../../../types/timeBox';
import { Event } from '../../../types/calendar-event';
import { calendarService } from '../../../services/api/calendar';
import { getCurrentUserId } from '../../../services/auth';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { getTimeBoxesByDate, getEventsByDateLocal, saveTimeBoxes, saveEvents } from '../../../services/indexedDB';
import { getSyncQueue, SyncQueueItem } from '../../../services/syncQueueService';
import { logDev, logWarn } from '../../../utils/logger';

// 幫助函數：將同步佇列中的變更合併到數據中
const mergeItemsWithSyncQueue = (items: ScheduleItem[], queue: SyncQueueItem[]): ScheduleItem[] => {
  let merged = [...items];

  // 只處理 TimeBox 相關的操作
  const timeBoxQueue = queue.filter(q => q.operation.includes('TIMEBOX'));

  // 按時間順序處理操作
  timeBoxQueue.sort((a, b) => a.timestamp - b.timestamp);

  for (const q of timeBoxQueue) {
    if (q.operation === 'CREATE_TIMEBOX' && q.data && q.timeBoxId) {
      // 檢查是否已存在 (避免重複添加)
      const exists = merged.find(i => i.id === q.timeBoxId);
      if (!exists) {
        // 對於 Create，data 通常包含完整對象
        // 確保有 itemType
        const newItem = { ...q.data, itemType: 'timebox' } as TimeBox;
        merged.push(newItem);
      }
    } else if (q.operation === 'UPDATE_TIMEBOX' && q.timeBoxId && q.data) {
      const index = merged.findIndex(i => i.id === q.timeBoxId);
      if (index !== -1) {
        // 合併更新
        merged[index] = { ...merged[index], ...q.data } as TimeBox;
      }
    } else if (q.operation === 'DELETE_TIMEBOX' && q.timeBoxId) {
      // 移除項目
      merged = merged.filter(i => i.id !== q.timeBoxId);
    }
  }

  return merged;
};

/**
 * 將 ScheduleItems 分類並快取到 IndexedDB（背景同步）
 * @param userId - 用戶 ID
 * @param items - 要快取的項目（TimeBox + Event）
 * @param context - 日誌上下文（如：「網路載入」、「即時監聽」）
 */
const syncItemsToIndexedDB = (
  userId: string,
  items: ScheduleItem[],
  context: string
): void => {
  // TimeBox 快取
  const timeBoxItems = items.filter(item =>
    'category' in item && !('timeType' in item)
  ) as TimeBox[];

  if (timeBoxItems.length > 0) {
    saveTimeBoxes(userId, timeBoxItems).catch(err => {
      logWarn('Calendar', `TimeBox 快取失敗（${context}）`, err);
    });
  }

  // Event 快取
  const eventItems = items.filter(item =>
    'timeType' in item
  ) as Event[];

  if (eventItems.length > 0) {
    saveEvents(userId, eventItems).catch(err => {
      logWarn('Calendar', `Event 快取失敗（${context}）`, err);
    });
  }
};

interface UseCalendarItemsOptions {
  /** 是否使用即時監聽 */
  useRealtime?: boolean;
  /** 日期範圍 (如果不提供，則只獲取指定日期) */
  dateRange?: {
    startDate: Date;
    endDate: Date;
  };
  /** 是否自動重新加載 */
  autoRefresh?: boolean;
}

interface UseCalendarItemsReturn {
  /** 所有日程項目 */
  scheduleItems: ScheduleItem[];
  /** TimeBox 項目 */
  timeBoxes: TimeBox[];
  /** Event 項目 */
  events: Event[];
  /** 當前時間的項目 */
  currentItem: ScheduleItem | null;
  /** 下一個項目 */
  nextItem: ScheduleItem | null;
  /** 是否正在加載 */
  isLoading: boolean;
  /** 是否已完成首次載入（IndexedDB 或網路） */
  isInitialized: boolean;
  /** 是否正在重新整理 */
  isRefreshing: boolean;
  /** 錯誤信息 */
  error: Error | null;
  /** 手動刷新 */
  refresh: () => Promise<void>;
  /** 檢查時間衝突 */
  checkConflicts: (startTime: Date, endTime: Date, excludeIds?: { timeBoxIds?: string[], eventIds?: string[] }) => Promise<{
    timeBoxConflicts: TimeBox[];
    eventConflicts: Event[];
    hasConflicts: boolean;
  }>;
}

export const useCalendarItems = (
  date: Date,
  options: UseCalendarItemsOptions = {}
): UseCalendarItemsReturn => {
  const {
    useRealtime = true,
    dateRange,
    autoRefresh = true
  } = options;

  // ======================================================
  // 狀態管理
  // ======================================================
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [currentItem, setCurrentItem] = useState<ScheduleItem | null>(null);
  const [nextItem, setNextItem] = useState<ScheduleItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 內部狀態
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isFetchingRef = useRef(false);
  const loadDataRef = useRef<typeof loadData | null>(null);
  const findCurrentAndNextRef = useRef<typeof findCurrentAndNext | null>(null);
  const setupRealtimeSubscriptionRef = useRef<typeof setupRealtimeSubscription | null>(null);

  // ======================================================
  // 計算派生數據
  // ======================================================
  const timeBoxes: TimeBox[] = scheduleItems.filter(item =>
    'category' in item && !('timeType' in item)
  ) as TimeBox[];

  const events: Event[] = scheduleItems.filter(item =>
    'timeType' in item
  ) as Event[];

  // ======================================================
  // 工具函數
  // ======================================================
  const findCurrentAndNext = useCallback((items: ScheduleItem[]) => {
    const now = new Date();
    let current: ScheduleItem | null = null;
    let next: ScheduleItem | null = null;

    for (const item of items) {
      const itemEnd = item.endTime || item.startTime;

      // 檢查是否為當前項目 (進行中)
      // 排除全天事件，全天事件不應該顯示為「現在行程」
      if (item.startTime <= now && itemEnd > now) {
        // 如果是事件且為全天事件，則跳過
        if ('timeType' in item && item.timeType === 'allday') {
          continue;
        }
        current = item;
      }

      // 檢查是否為下一個項目 (未來的最近項目)
      if (item.startTime > now && !next) {
        next = item;
      }
    }

    setCurrentItem(current);
    setNextItem(next);
  }, []);

  // ======================================================
  // 🔥 Local-first: 從 IndexedDB 載入快取
  // ======================================================
  const loadFromIndexedDB = useCallback(async (forceUpdate = false) => {
    const userId = getCurrentUserId();
    if (!userId) return;

    try {
      // 目前只快取單日的 TimeBox 和 Event（dateRange 場景較複雜，暫不處理）
      if (!dateRange) {
        const [cachedTimeBoxes, cachedEvents] = await Promise.all([
          getTimeBoxesByDate(userId, date),
          getEventsByDateLocal(userId, date).catch(() => []),
        ]);
        logDev('Calendar', `從 IndexedDB 載入 ${cachedTimeBoxes.length} 個 TimeBox、${cachedEvents.length} 個 Event（離線優先）`);

        // 🔥 合併同步佇列中的待處理變更（離線支援的關鍵！）
        let items = [...cachedTimeBoxes, ...cachedEvents].sort(
          (a, b) => a.startTime.getTime() - b.startTime.getTime()
        ) as ScheduleItem[];
        try {
          const syncQueue = await getSyncQueue();
          if (syncQueue.length > 0) {
            items = mergeItemsWithSyncQueue(items, syncQueue);
            logDev('Calendar', `(IndexedDB) 已合併 ${syncQueue.length} 個待同步項目到視圖`);
          }
        } catch (mergeErr) {
          logWarn('Calendar', '(IndexedDB) 合併同步佇列失敗', mergeErr);
        }

        // 有資料才更新畫面（避免覆蓋較新的即時資料），forceUpdate 強制覆蓋（用於 refresh）
        if (items.length > 0 || forceUpdate) {
          setScheduleItems(prevItems => {
            if (forceUpdate || prevItems.length === 0) {
              if (findCurrentAndNextRef.current) {
                findCurrentAndNextRef.current(items);
              }
              return items;
            }
            return prevItems;
          });
          // IndexedDB 有資料 → 立即關 loading，網路更新會靜默刷新
          setIsLoading(false);
          setIsInitialized(true);
        }
        // IndexedDB 沒資料 → 維持 loading，等網路資料回填後再關
      }
    } catch (err) {
      logWarn('Calendar', 'IndexedDB 載入失敗，將使用網路資料', err);
    }
  }, [date, dateRange]);

  // ======================================================
  // 數據加載（網路）
  // ======================================================
  const loadData = useCallback(async (isRefresh = false) => {
    const userId = getCurrentUserId();
    if (!userId) {
      setError(new Error('用戶未登錄'));
      setIsLoading(false);
      return;
    }

    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (isRefresh) {
      setIsRefreshing(true);
    }
    // 非 refresh 的網路請求也不設 isLoading(true)：
    // IndexedDB 有資料時已關 loading；沒資料時 loading 維持 true 等這裡的 finally 關掉

    try {
      let items: ScheduleItem[];

      if (dateRange) {
        items = await calendarService.getScheduleItems(
          userId,
          dateRange.startDate,
          dateRange.endDate
        );
      } else {
        items = await calendarService.getScheduleItemsByDate(userId, date);
      }

      setScheduleItems(items);

      // 🔥 Local-first Merge: 合併同步佇列中的待處理變更
      try {
        const syncQueue = await getSyncQueue();
        if (syncQueue.length > 0) {
          const mergedItems = mergeItemsWithSyncQueue(items, syncQueue);
          setScheduleItems(mergedItems);
          items = mergedItems; // 更新 items 引用以便後續存儲
          logDev('Calendar', `已合併 ${syncQueue.length} 個待同步項目到視圖`);
        }
      } catch (mergeErr) {
        logWarn('Calendar', '合併同步佇列失敗', mergeErr);
      }

      if (findCurrentAndNextRef.current) {
        findCurrentAndNextRef.current(items);
      }
      setError(null);

      // 🔥 Local-first: 將網路資料同步到 IndexedDB
      if (!dateRange) {
        syncItemsToIndexedDB(userId, items, '網路載入');
      }
    } catch (err) {
      logWarn('Calendar', '載入日程項目失敗', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
      setIsRefreshing(false);
      isFetchingRef.current = false;
    }
  }, [date, dateRange]); // 移除 findCurrentAndNext 依賴避免循環

  // 更新 refs
  loadDataRef.current = loadData;
  findCurrentAndNextRef.current = findCurrentAndNext;
  const loadFromIndexedDBRef = useRef<typeof loadFromIndexedDB | null>(null);
  loadFromIndexedDBRef.current = loadFromIndexedDB;

  // ======================================================
  // 即時監聽
  // ======================================================
  const setupRealtimeSubscription = useCallback(async () => {
    const userId = getCurrentUserId();
    if (!userId || !useRealtime) return;

    try {
      let unsubscribe: (() => void);

      if (dateRange) {
        unsubscribe = await calendarService.subscribeToScheduleItems(
          userId,
          dateRange.startDate,
          dateRange.endDate,
          {
            onSnapshot: (items) => {
              // 🔥 Local-first Merge: 合併同步佇列 (Realtime)
              getSyncQueue().then(syncQueue => {
                let finalItems = items;
                if (syncQueue.length > 0) {
                  finalItems = mergeItemsWithSyncQueue(items, syncQueue);
                  logDev('Calendar', `(RT) 已合併 ${syncQueue.length} 個待同步項目`);
                }

                setScheduleItems(finalItems);
                if (findCurrentAndNextRef.current) {
                  findCurrentAndNextRef.current(finalItems);
                }
                setError(null);
                setIsLoading(false);

                // 🔥 Local-first: 將即時監聽的資料 (合併後) 同步到 IndexedDB
                syncItemsToIndexedDB(userId, finalItems, '即時監聽（範圍）');
              }).catch(err => {
                // Fallback if queue fetch fails
                logWarn('Calendar', '合併同步佇列失敗（即時監聽），使用原始資料', err);
                setScheduleItems(items);
                if (findCurrentAndNextRef.current) {
                  findCurrentAndNextRef.current(items);
                }
                setError(null);
                setIsLoading(false);
              });
            },
            onError: (err) => {
              logWarn('Calendar', '即時監聽錯誤（dateRange）', err);
              setError(err);
              // 降級到定時查詢
              if (autoRefresh && loadDataRef.current) {
                loadDataRef.current();
              }
            }
          }
        );
      } else {
        // 對於單日查詢,使用日期範圍監聽
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);

        unsubscribe = await calendarService.subscribeToScheduleItems(
          userId,
          startOfDay,
          endOfDay,
          {
            onSnapshot: (items) => {
              // 🔥 Local-first Merge: 合併同步佇列 (Realtime)
              getSyncQueue().then(syncQueue => {
                let finalItems = items;
                if (syncQueue.length > 0) {
                  finalItems = mergeItemsWithSyncQueue(items, syncQueue);
                  logDev('Calendar', `(RT) 已合併 ${syncQueue.length} 個待同步項目`);
                }

                setScheduleItems(finalItems);
                if (findCurrentAndNextRef.current) {
                  findCurrentAndNextRef.current(finalItems);
                }
                setError(null);
                setIsLoading(false);

                // 🔥 Local-first: 將即時監聽的資料 (合併後) 同步到 IndexedDB
                syncItemsToIndexedDB(userId, finalItems, '即時監聽');
              }).catch(err => {
                // Fallback
                logWarn('Calendar', '合併同步佇列失敗（即時監聽），使用原始資料', err);
                setScheduleItems(items);
                if (findCurrentAndNextRef.current) {
                  findCurrentAndNextRef.current(items);
                }
                setError(null);
                setIsLoading(false);
              });
            },
            onError: (err) => {
              logWarn('Calendar', '即時監聽錯誤', err);
              setError(err);
              // 降級到定時查詢
              if (autoRefresh && loadDataRef.current) {
                loadDataRef.current();
              }
            }
          }
        );
      }

      unsubscribeRef.current = unsubscribe;
    } catch (err) {
      logWarn('Calendar', '設置即時監聽失敗', err);
      // 降級到手動加載
      if (loadDataRef.current) {
        loadDataRef.current();
      }
    }
  }, [date, dateRange, useRealtime, autoRefresh]); // 移除函數依賴避免循環

// 更新 setupRealtimeSubscription ref
setupRealtimeSubscriptionRef.current = setupRealtimeSubscription;

// ======================================================
// 衝突檢查
// ======================================================
const checkConflicts = useCallback(async (
  startTime: Date,
  endTime: Date,
  excludeIds?: { timeBoxIds?: string[], eventIds?: string[] }
) => {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error('用戶未登錄');
  }

  return calendarService.checkScheduleConflicts(
    userId,
    startTime,
    endTime,
    excludeIds
  );
}, []);

// ======================================================
// 生命周期
// ======================================================
useEffect(() => {
  // 🔥 Local-first: 先從 IndexedDB 載入，立即顯示快取資料
  if (loadFromIndexedDBRef.current) {
    loadFromIndexedDBRef.current();
  }

  // 然後設置即時監聽或載入網路資料
  if (useRealtime && setupRealtimeSubscriptionRef.current) {
    setupRealtimeSubscriptionRef.current();
  } else if (loadDataRef.current) {
    loadDataRef.current();
  }

  return () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  };
}, [useRealtime, date, dateRange]); // 移除函數依賴避免循環

// ======================================================
// 事件總線監聽
// ======================================================
useEffect(() => {
  const handleTimeBoxUpdate = () => {
    if (!useRealtime && autoRefresh && loadDataRef.current) {
      // 使用 ref 避免閉包問題
      loadDataRef.current(true);
    }
  };

  const handleEventUpdate = () => {
    if (!useRealtime && autoRefresh && loadDataRef.current) {
      // 使用 ref 避免閉包問題
      loadDataRef.current(true);
    }
  };

  // 專門處理 scheduleChanged 事件（不受 autoRefresh 限制）
  const handleScheduleChanged = () => {
    // 用戶主動觸發的變更應該立即刷新，不檢查 autoRefresh
    if (!useRealtime && loadDataRef.current) {
      loadDataRef.current(true);
    }
  };

  // 處理同步完成事件（從離線恢復後的自動同步）
  const handleSyncCompleted = () => {
    // 同步完成後刷新數據，包含從 IndexedDB 和網路
    if (loadFromIndexedDBRef.current) {
      loadFromIndexedDBRef.current(true); // 先從 IndexedDB 刷新
    }
    if (loadDataRef.current) {
      loadDataRef.current(true); // 再從網路刷新
    }
  };

  // 監聽 TimeBox 相關事件
  eventBus.on(EVENTS.TIMEBOX.CREATED, handleTimeBoxUpdate);
  eventBus.on(EVENTS.TIMEBOX.UPDATED, handleTimeBoxUpdate);
  eventBus.on(EVENTS.TIMEBOX.DELETED, handleTimeBoxUpdate);

  // 監聽 Event 相關事件 (如果存在)
  if (EVENTS.EVENT) {
    eventBus.on(EVENTS.EVENT.CREATED, handleEventUpdate);
    eventBus.on(EVENTS.EVENT.UPDATED, handleEventUpdate);
    eventBus.on(EVENTS.EVENT.DELETED, handleEventUpdate);
  }

  // 監聽排程變更事件（來自 Morning Kickoff 的調整操作）
  eventBus.on('scheduleChanged', handleScheduleChanged);

  // 監聽同步完成事件
  eventBus.on('syncCompleted', handleSyncCompleted);

  return () => {
    eventBus.off(EVENTS.TIMEBOX.CREATED, handleTimeBoxUpdate);
    eventBus.off(EVENTS.TIMEBOX.UPDATED, handleTimeBoxUpdate);
    eventBus.off(EVENTS.TIMEBOX.DELETED, handleTimeBoxUpdate);

    if (EVENTS.EVENT) {
      eventBus.off(EVENTS.EVENT.CREATED, handleEventUpdate);
      eventBus.off(EVENTS.EVENT.UPDATED, handleEventUpdate);
      eventBus.off(EVENTS.EVENT.DELETED, handleEventUpdate);
    }

    // 移除排程變更事件監聽器
    eventBus.off('scheduleChanged', handleScheduleChanged);
    eventBus.off('syncCompleted', handleSyncCompleted);
  };
}, [useRealtime, autoRefresh]); // 移除 loadData 依賴，避免循環

// ======================================================
// 定時更新當前項目
// ======================================================
useEffect(() => {
  if (scheduleItems.length === 0) return;

  const interval = setInterval(() => {
    if (findCurrentAndNextRef.current) {
      findCurrentAndNextRef.current(scheduleItems);
    }
  }, 30000); // 每30秒更新一次當前項目

  return () => clearInterval(interval);
}, [scheduleItems]); // 移除函數依賴避免循環

// 創建穩定的 refresh 函數
const refresh = useCallback(async () => {
  // 🔥 離線優先：先從 IndexedDB 強制重新載入
  if (loadFromIndexedDBRef.current) {
    await loadFromIndexedDBRef.current(true); // forceUpdate = true
  }

  // 只在線上時才嘗試從網路更新
  if (navigator.onLine && loadDataRef.current) {
    await loadDataRef.current(true);
  }
}, []);

return {
  scheduleItems,
  timeBoxes,
  events,
  currentItem,
  nextItem,
  isLoading,
  isInitialized,
  isRefreshing,
  error,
  refresh,
  checkConflicts,
};
};